import { useState } from 'react';
import type { DiagramMetadata } from '../../types/barDiagram';

type EditableField = 'localitat' | 'cruilla' | 'projecte' | 'data' | 'regulador' | 'planol' | 'modificat';

interface Props {
  metadata: DiagramMetadata;
  editable?: boolean;
  onMetadataChange?: (field: EditableField, value: string) => void;
}

export function BarDiagramInfo({ metadata, editable, onMetadataChange }: Props) {
  const [editingField, setEditingField] = useState<EditableField | null>(null);
  const [fieldValue, setFieldValue] = useState('');

  const startEdit = (field: EditableField) => {
    if (!editable) return;
    setEditingField(field);
    setFieldValue(metadata[field]);
  };

  const commitEdit = () => {
    if (editingField && onMetadataChange) {
      onMetadataChange(editingField, fieldValue);
    }
    setEditingField(null);
  };

  const cancelEdit = () => setEditingField(null);

  const renderValue = (field: EditableField, colSpan?: number) => {
    const isEditing = editingField === field;
    const value = metadata[field];

    const style: React.CSSProperties = {
      ...valueStyle,
      ...(editable ? { cursor: 'pointer' } : {}),
      ...(colSpan ? {} : {}),
    };

    if (isEditing) {
      return (
        <th colSpan={colSpan} style={style}>
          <input
            autoFocus
            value={fieldValue}
            onChange={e => setFieldValue(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={e => {
              if (e.key === 'Enter') commitEdit();
              if (e.key === 'Escape') cancelEdit();
            }}
            style={{
              width: '100%',
              textAlign: 'center',
              fontSize: 13,
              border: '1px solid #3b82f6',
              borderRadius: 2,
              outline: 'none',
              padding: '1px 4px',
              fontFamily: 'inherit',
            }}
          />
        </th>
      );
    }

    return (
      <th colSpan={colSpan} style={style} onClick={() => startEdit(field)}>
        {value || (editable ? <span style={{ color: '#aaa', fontStyle: 'italic', fontWeight: 'normal' }}>(clic per editar)</span> : '')}
      </th>
    );
  };

  return (
    <table
      style={{
        borderCollapse: 'collapse',
        width: '100%',
        fontFamily: 'Arial, Helvetica, sans-serif',
        fontSize: '13px',
        marginTop: 16,
        color: 'black',
      }}
    >
      <tbody>
        <tr>
          <th
            rowSpan={3}
            style={{
              border: '1px solid black',
              padding: '8px 12px',
              width: 130,
              verticalAlign: 'middle',
              textAlign: 'center',
              backgroundColor: '#f9f9f9',
            }}
          >
            <img src="/logo-ars93.jpg" alt="ARS_93" style={{ width: 100, height: 40 }} />
          </th>
          <th style={labelStyle}>Localitat:</th>
          {renderValue('localitat', 3)}
          <th style={labelStyle}>N.Cruilla:</th>
          {renderValue('cruilla')}
        </tr>
        <tr>
          <th style={labelStyle}>Projecte:</th>
          {renderValue('projecte')}
          <th colSpan={2} rowSpan={2} style={{
            ...valueStyle,
            textAlign: 'left',
            paddingLeft: 12,
          }}>
            <div>Diagrama de Fases - Grups Interseccio:</div>
            <div style={{ fontWeight: 'bold', fontSize: 15, marginTop: 4 }}>
              {metadata.cruilla}
            </div>
          </th>
          <th style={labelStyle}>Regulador:</th>
          {renderValue('regulador')}
        </tr>
        <tr>
          <th style={labelStyle}>Data:</th>
          {renderValue('data')}
        </tr>
        <tr>
          <th style={{
            border: '1px solid black',
            padding: '8px 12px',
            verticalAlign: 'middle',
            textAlign: 'center',
            backgroundColor: '#f9f9f9',
            fontSize: 11,
            color: '#666',
          }}>
            Diagrama Barras
          </th>
          <th style={labelStyle}>N. Planol:</th>
          {renderValue('planol')}
          <th style={labelStyle}>Modificat:</th>
          {renderValue('modificat', 2)}
        </tr>
      </tbody>
    </table>
  );
}

const labelStyle: React.CSSProperties = {
  border: '1px solid black',
  padding: '4px 8px',
  textAlign: 'right',
  backgroundColor: '#f5f5f5',
  fontWeight: 'bold',
  whiteSpace: 'nowrap',
};

const valueStyle: React.CSSProperties = {
  border: '1px solid black',
  padding: '4px 8px',
  textAlign: 'center',
  fontWeight: 'normal',
};
